import './styles.scss';

import React from 'react';
import PropTypes from 'prop-types';

import Spinner from 'Components/layout/Spinner';
import ErrorMessage from 'Components/forms2/ErrorMessage';

export default class FormComponent extends React.PureComponent {
    static propTypes = {
        children: PropTypes.any,
        className: PropTypes.string,
        onStateChange: PropTypes.func,
        isPending: PropTypes.bool,
        onSubmit: PropTypes.func,
        data: PropTypes.object,
        errors: PropTypes.arrayOf(
            PropTypes.shape({
                field: PropTypes.string,
                message: PropTypes.string
            })
        )
    };
    static defaultProps = {
        className: '',
        isPending: false,
        data: {},
        errors: []
    }

    supportedComponents = [
        'FormInputComponent',
        'FormCheckboxComponent',
        'FormSelectComponent',
        'FormEditorComponent',
        'FormTextareaComponent',
        'FormDatePickerComponent',
        'FormRadioComponent',
        'FormSimpleManagableListComponent',
        'FormSliderComponent'
    ];
    components = {};
    state = {
        errors: {}
    };

    constructor(props) {
        super(props);
        this.traverseChildren(props.children);
    }

    componentWillReceiveProps = nextProps => {
        if (JSON.stringify(this.props.errors) !== JSON.stringify(nextProps.errors)) {
            this.setState(prevState => ({
                ...prevState,
                errors: this.transformErrors(nextProps.errors)
            }));
        }
    }

    transformErrors = errorsArray => {
        let errors = {};

        if (Array.isArray(errorsArray)) {
            errorsArray.forEach(error => {
                if (this.components.hasOwnProperty(error.field)) {
                    errors[error.field] = error.message || 'Wrong value';
                }
            });

            if (errorsArray.length && !Object.keys(errors).length) {
                process.rollbar.error('Undefined form error', errorsArray);
                errors.global = 'We\'re sorry, something went wrong. Please try again later or contact support@foap.com.';
            }
        } else {
            process.rollbar.error('Undefined form error', errorsArray);
            errors.global = 'We\'re sorry, something went wrong. Please try again later or contact support@foap.com.';
        }

        return errors;
    }

    traverseChildren = children => {
        if (Array.isArray(children)) {
            return children.map(child => this.validateChild(child));
        } else if (typeof children === 'object') {
            return this.validateChild(children);
        } else {
            return children;
        }
    }

    validateChild = child => {
        if (!child) return null;

        if (
            child.hasOwnProperty('type') && typeof child.type === 'function' &&
            child.type.hasOwnProperty('displayName') && this.supportedComponents.indexOf(child.type.displayName) > -1
        ) {
            child = this.injectChildProps(child);

            //Add component to tracked components object
            this.components = {
                ...this.components,
                [child.props.name]: child
            };
        }

        if (child.props.hasOwnProperty('children')) {
            return {
                ...child,
                props: {
                    ...child.props,
                    children: this.traverseChildren(child.props.children)
                }
            };
        } else {
            return { ...child };
        }
    }

    getDefaultValue = child => {
        switch (child.type.displayName) {
            case 'FormCheckboxComponent':
                return false;
            case 'FormSimpleManagableListComponent':
                return [];
            default:
                return null;
        }
    }

    injectChildProps = child => {
        const { data } = this.props;
        const { errors } = this.state;

        //Inject child props
        return {
            ...child,
            props: {
                ...child.props,
                value: data[child.props.name],
                errorMessage: errors.hasOwnProperty(child.props.name)
                    ? Array.isArray(errors[child.props.name])
                        ? errors[child.props.name].join(', ')
                        : typeof errors[child.props.name] === 'string'
                            ? errors[child.props.name]
                            : ''
                    : '',
                onFormChange: this.onChange
            }
        };
    }

    validateInput = (component, value) => {
        //Vaidate required state
        const isRequired = Boolean(component.props.required) || false;
        const maxChars = component.props.maxChars || 0;
        let errors = [];

        if (component.type === 'FormSimpleManagableListComponent') {
            if (isRequired && !value.length) {
                if (component.props.customRequiredError) {
                    errors.push(component.props.customRequiredError);
                } else {
                    errors.push('You have to provide value');
                }
            }
        } else {
            if (isRequired && !value) {
                if (component.props.customRequiredError) {
                    errors.push(component.props.customRequiredError);
                } else {
                    switch (component.type) {
                        case 'FormCheckboxComponent':
                            errors.push('You have to check option');
                            break;
                        case 'FormSelectComponent':
                            errors.push('You have to select option');
                            break;

                        default:
                            errors.push('You have to provide value');
                            break;
                    }
                }
            }

            if (maxChars && value.toString().length > maxChars) {
                errors.push('You have exceeded alowed length');
            }
        }

        return errors;
    }

    onSubmit = event => {
        event.preventDefault();

        let errors = {};
        Object.keys(this.components).forEach(name => {
            const inputError = this.validateInput(this.components[name], this.props.data[name]);
            if (inputError.length) {
                errors[name] = inputError;
            }
        });

        if (Object.keys(errors).length) {
            //Set form errors
            this.setState(prevState => ({
                ...prevState,
                errors: errors
            }));
        } else {
            //Submit form
            if (this.props.onSubmit) {
                this.props.onSubmit(this.props.data);
            }
        }
        return this.props.data;
    }

    onChange = data => {
        //Remove previous errors
        const inputError = this.validateInput(this.components[data.name], data.value);

        //Set new form state
        this.setState((prevState, props) => ({
            ...prevState,
            errors: {
                ...prevState.errors,
                [data.name]: inputError
            }
        }));

        this.props.onStateChange(data.name, data.value);

        return data;
    }

    render() {
        const { className, isPending, children } = this.props;
        const { errors } = this.state;
        const globalError = errors.hasOwnProperty('global') ? errors.global : null;

        return (
            <form className={`component-form ${className}`} onSubmit={this.onSubmit} noValidate>
                {isPending ? <Spinner /> : null}
                {globalError ? <ErrorMessage message={globalError} mode="global" /> : null}
                {this.traverseChildren(children)}
            </form>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/forms2/Form/index.js